import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Plus, Upload } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

export const inputCls = "w-full bg-ink border border-white/10 px-3 py-2.5 text-sm text-ivory placeholder:text-white/25 focus:border-gold/60 focus:outline-none";
const labelCls = "block font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1.5";

export const STAGES = ["CONCEPT", "IN DEVELOPMENT", "PHASE 1", "PILOT", "ACTIVE", "FUTURE"];

export function UploadField({ value, onChange, testid, accept = "image/*", privateUpload = false }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(privateUpload ? "/admin/upload?private=true" : "/admin/upload", fd);
      onChange(privateUpload ? data.id : data.url, data);
      toast.success(privateUpload ? "Confidential file stored securely" : "File uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex gap-2 items-center">
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder="https://… or upload" className={inputCls} />
      <label data-testid={`${testid}-upload`} className="shrink-0 cursor-pointer border border-white/15 px-3 py-2.5 text-white/50 hover:border-gold/50 hover:text-gold transition-colors">
        <Upload size={15} />
        <input type="file" accept={accept} className="hidden" onChange={upload} disabled={busy} />
      </label>
    </div>
  );
}

const EMPTY_PROJECT = { name: "", category: "", market: "", description: "", stage: "CONCEPT", image_url: "", website: "", details: "", category_pt: "", market_pt: "", description_pt: "", details_pt: "", page_enabled: false, page_url: "", hidden: false };

export function ProjectsPanel({ projects, reload }) {
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(EMPTY_PROJECT);

  const startEdit = (p) => { setForm({ ...EMPTY_PROJECT, ...p }); setEditing(p.id); };
  const startNew = () => { setForm({ ...EMPTY_PROJECT, order: projects.length }); setEditing("new"); };

  const save = async () => {
    try {
      if (editing === "new") await api.post("/admin/projects", form);
      else await api.put(`/admin/projects/${editing}`, form);
      toast.success("Project saved");
      setEditing(null);
      reload();
    } catch {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/projects/${id}`);
    toast.success("Project deleted");
    reload();
  };

  const toggleHidden = async (p) => {
    await api.put(`/admin/projects/${p.id}`, { ...p, hidden: !p.hidden });
    reload();
  };

  const move = async (index, dir) => {
    const ids = projects.map((p) => p.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    await api.post("/admin/projects/reorder", ids);
    reload();
  };

  return (
    <div data-testid="projects-panel">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif text-2xl text-ivory">Projects</h2>
        <button data-testid="project-add-btn" onClick={startNew} className="flex items-center gap-2 px-4 py-2.5 bg-gold text-ink font-mono text-[10px] tracking-[0.2em] uppercase hover:bg-gold-light transition-colors">
          <Plus size={13} /> Add Project
        </button>
      </div>

      {editing !== null && (
        <div data-testid="project-form" className="border border-gold/30 bg-surface p-6 mb-8 grid sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>Name</label><input data-testid="project-name-input" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><label className={labelCls}>Category</label><input data-testid="project-category-input" className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
          <div><label className={labelCls}>Market</label><input data-testid="project-market-input" className={inputCls} value={form.market} onChange={(e) => setForm({ ...form, market: e.target.value })} /></div>
          <div><label className={labelCls}>Stage</label>
            <select data-testid="project-stage-select" className={inputCls} value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}>
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2"><label className={labelCls}>Description</label><textarea data-testid="project-description-input" rows={2} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Project Details</label><textarea data-testid="project-details-input" rows={2} className={inputCls} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} /></div>
          <div><label className={labelCls}>Cover Image</label><UploadField testid="project-image-input" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} /></div>
          <div><label className={labelCls}>Website</label><input data-testid="project-website-input" className={inputCls} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
          <div className="sm:col-span-2 border-t border-white/10 pt-4 mt-2">
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-gold mb-3">Português (PT) — optional translations</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Category (PT)</label><input data-testid="project-category-pt-input" className={inputCls} value={form.category_pt || ""} onChange={(e) => setForm({ ...form, category_pt: e.target.value })} /></div>
              <div><label className={labelCls}>Market (PT)</label><input data-testid="project-market-pt-input" className={inputCls} value={form.market_pt || ""} onChange={(e) => setForm({ ...form, market_pt: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Description (PT)</label><textarea data-testid="project-description-pt-input" rows={2} className={inputCls} value={form.description_pt || ""} onChange={(e) => setForm({ ...form, description_pt: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Project Details (PT)</label><textarea data-testid="project-details-pt-input" rows={2} className={inputCls} value={form.details_pt || ""} onChange={(e) => setForm({ ...form, details_pt: e.target.value })} /></div>
            </div>
          </div>
          <div className="sm:col-span-2 border-t border-white/10 pt-4 mt-2">
            <label className="flex items-center gap-3 text-sm text-white/60">
              <input data-testid="project-page-enabled-toggle" type="checkbox" checked={!!form.page_enabled} onChange={(e) => setForm({ ...form, page_enabled: e.target.checked })} className="accent-[#D4AF37]" />
              Dedicated Project Page (e.g. nubai.bsgotech.com)
            </label>
            {form.page_enabled && (
              <div className="mt-3"><label className={labelCls}>Project URL</label><input data-testid="project-page-url-input" placeholder="https://…" className={inputCls} value={form.page_url || ""} onChange={(e) => setForm({ ...form, page_url: e.target.value })} /></div>
            )}
            <p className="text-xs text-white/30 mt-2">When ON with a valid URL, the public site shows an "Explore" button for this project. Only enable for URLs that actually exist.</p>
          </div>
          <div className="sm:col-span-2 flex gap-3 mt-2">
            <button data-testid="project-save-btn" onClick={save} className="px-6 py-2.5 bg-gold text-ink font-mono text-[10px] tracking-[0.2em] uppercase hover:bg-gold-light">Save</button>
            <button data-testid="project-cancel-btn" onClick={() => setEditing(null)} className="px-6 py-2.5 border border-white/15 text-white/60 font-mono text-[10px] tracking-[0.2em] uppercase hover:border-gold/50">Cancel</button>
          </div>
        </div>
      )}

      <div className="divide-y divide-white/10 border border-white/10">
        {projects.map((p, i) => (
          <div key={p.id} data-testid={`project-row-${p.name.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-4 px-5 py-4 bg-ink hover:bg-surface/60 transition-colors">
            {p.image_url && <img src={fileUrl(p.image_url)} alt="" className="w-12 h-12 object-cover border border-white/10 shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="text-ivory font-medium truncate">{p.name} {p.hidden && <span className="text-white/30 text-xs">(hidden)</span>}</p>
              <p className="text-xs text-white/40 font-mono tracking-wider">{p.stage} · {p.market}</p>
            </div>
            <button data-testid={`project-up-${p.id}`} onClick={() => move(i, -1)} className="text-white/40 hover:text-gold p-1"><ArrowUp size={15} /></button>
            <button data-testid={`project-down-${p.id}`} onClick={() => move(i, 1)} className="text-white/40 hover:text-gold p-1"><ArrowDown size={15} /></button>
            <button data-testid={`project-hide-${p.id}`} onClick={() => toggleHidden(p)} className="text-white/40 hover:text-gold p-1">{p.hidden ? <EyeOff size={15} /> : <Eye size={15} />}</button>
            <button data-testid={`project-edit-${p.id}`} onClick={() => startEdit(p)} className="text-white/40 hover:text-gold p-1"><Pencil size={15} /></button>
            <button data-testid={`project-delete-${p.id}`} onClick={() => remove(p.id)} className="text-white/40 hover:text-red-400 p-1"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
